import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth, hasRole } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import StatusPill from '@/components/StatusPill';
import { ChartBox } from '@/components/OpsUI';
import { AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { ArrowLeft, RefreshCw, Pause, Play, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

export default function ServiceDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = hasRole(user, ['admin', 'tech']);
  const [service, setService] = useState(null);
  const [history, setHistory] = useState([]);
  const [uptime, setUptime] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [deployments, setDeployments] = useState([]);

  const load = async () => {
    try {
      const [svc, hist, up, inc, lg, dep] = await Promise.all([
        api.get(`/services/${id}`),
        api.get(`/services/${id}/history`, { params: { limit: 80 } }),
        api.get(`/services/${id}/uptime`, { params: { hours: 24 } }),
        api.get('/incidents', { params: { service_id: id } }),
        api.get('/logs', { params: { service_id: id, limit: 50 } }),
        api.get('/deployments', { params: { service_id: id } }),
      ]);
      setService(svc.data);
      setHistory(hist.data.reverse());
      setUptime(up.data);
      setIncidents(inc.data);
      setLogs(lg.data);
      setDeployments(dep.data);
    } catch (e) {
      toast.error('Error');
    }
  };

  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, [id]);

  if (!service) return <div className="text-sm text-[hsl(var(--muted-foreground))]">{t('loading')}</div>;

  const chartData = history.map((h) => ({
    t: h.checked_at ? new Date(h.checked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
    latency: h.latency_ms || 0,
    status: h.status,
  }));

  const check = async () => { try { await api.post(`/services/${id}/check`); toast.success(t('success')); load(); } catch (e) { toast.error(t('error')); } };
  const togglePause = async () => { await api.patch(`/services/${id}`, { paused: !service.paused }); load(); };
  const toggleMaint = async () => { await api.patch(`/services/${id}`, { maintenance_mode: !service.maintenance_mode }); load(); };

  return (
    <div className="space-y-6" data-testid="service-detail-page">
      <div>
        <Link to="/services" className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> {t('nav_services')}
        </Link>
        <div className="mt-2 flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold">{service.name}</h1>
            <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{service.description}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono mt-1">{service.health_url}</div>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={service.maintenance_mode ? 'maintenance' : service.paused ? 'paused' : (service.current_status || 'unknown')} name={service.id} />
            {canEdit && <>
              <Button variant="outline" size="sm" onClick={check} data-testid="service-detail-check"><RefreshCw className="h-3.5 w-3.5 mr-1" /> {t('service_check_now')}</Button>
              <Button variant="outline" size="sm" onClick={togglePause} data-testid="service-detail-pause">
                {service.paused ? <Play className="h-3.5 w-3.5 mr-1" /> : <Pause className="h-3.5 w-3.5 mr-1" />}
                {service.paused ? t('service_resume') : t('service_pause')}
              </Button>
              <Button variant="outline" size="sm" onClick={toggleMaint} data-testid="service-detail-maint">
                <Wrench className={`h-3.5 w-3.5 mr-1 ${service.maintenance_mode ? 'text-sky-400' : ''}`} />
                {t('service_maintenance')}
              </Button>
            </>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 bg-card border border-border">
          <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{t('kpi_uptime_24h')}</div>
          <div className="text-3xl font-display font-semibold mt-2 tabular-nums">{uptime?.uptime_pct || 0}%</div>
        </Card>
        <Card className="p-4 bg-card border border-border">
          <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Last latency</div>
          <div className="text-3xl font-display font-semibold mt-2 tabular-nums">{service.last_latency_ms ? `${Math.round(service.last_latency_ms)}ms` : '—'}</div>
        </Card>
        <Card className="p-4 bg-card border border-border">
          <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Total checks (24h)</div>
          <div className="text-3xl font-display font-semibold mt-2 tabular-nums">{uptime?.total_checks || 0}</div>
        </Card>
        <Card className="p-4 bg-card border border-border">
          <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Version</div>
          <div className="text-3xl font-display font-semibold mt-2 font-mono">v{service.version}</div>
        </Card>
      </div>

      <Card className="p-5 bg-card border border-border">
        <h2 className="font-display text-lg font-semibold mb-3">{t('latency_over_time')}</h2>
        <ChartBox height={260}>
            <AreaChart data={chartData} margin={{ left: -20, right: 12, top: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="sl" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00CCFF" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#00CCFF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="t" tick={{ fill: '#A7B6C6', fontSize: 11 }} stroke="rgba(255,255,255,0.08)" />
              <YAxis tick={{ fill: '#A7B6C6', fontSize: 11 }} stroke="rgba(255,255,255,0.08)" />
              <Tooltip contentStyle={{ background: '#0F1620', border: '1px solid rgba(0,204,255,0.25)', borderRadius: 8, color: '#EAF2F8' }} />
              <Area type="monotone" dataKey="latency" stroke="#00CCFF" strokeWidth={2} fill="url(#sl)" />
            </AreaChart>
        </ChartBox>
      </Card>

      <Tabs defaultValue="history" className="w-full">
        <TabsList>
          <TabsTrigger value="history" data-testid="tab-history">{t('service_history')}</TabsTrigger>
          <TabsTrigger value="incidents" data-testid="tab-incidents">{t('nav_incidents')} ({incidents.length})</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">{t('nav_logs')} ({logs.length})</TabsTrigger>
          <TabsTrigger value="deployments" data-testid="tab-deployments">{t('nav_deployments')} ({deployments.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="history">
          <Card className="bg-card border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border">
                  <th className="px-4 py-3">When</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Code</th><th className="px-4 py-3">Latency</th><th className="px-4 py-3">Error</th>
                </tr>
              </thead>
              <tbody>
                {[...history].reverse().slice(0, 50).map((h) => (
                  <tr key={h.id} className="border-b border-border/60 hover:bg-accent/40">
                    <td className="px-4 py-2 font-mono text-xs">{h.checked_at ? formatDistanceToNow(new Date(h.checked_at), { addSuffix: true }) : '—'}</td>
                    <td className="px-4 py-2"><StatusPill status={h.status} name={h.id} /></td>
                    <td className="px-4 py-2 font-mono text-xs">{h.response_code || '—'}</td>
                    <td className="px-4 py-2 font-mono tabular-nums">{h.latency_ms ? `${Math.round(h.latency_ms)} ms` : '—'}</td>
                    <td className="px-4 py-2 text-xs text-red-300">{h.error || '—'}</td>
                  </tr>
                ))}
                {history.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))]">{t('no_data')}</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
        <TabsContent value="incidents">
          <Card className="p-4 bg-card border border-border">
            {incidents.length === 0 ? <div className="text-center py-6 text-[hsl(var(--muted-foreground))] text-sm">{t('no_data')}</div> :
              <ul className="divide-y divide-border">
                {incidents.map((i) => (
                  <li key={i.id} className="py-3">
                    <div className="flex justify-between"><div className="font-medium">{i.title}</div><span className="text-xs text-[hsl(var(--muted-foreground))] uppercase">{i.severity}</span></div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{i.status}</div>
                  </li>
                ))}
              </ul>
            }
          </Card>
        </TabsContent>
        <TabsContent value="logs">
          <Card className="p-4 bg-card border border-border">
            {logs.length === 0 ? <div className="text-center py-6 text-[hsl(var(--muted-foreground))] text-sm">{t('no_data')}</div> :
              <ul className="divide-y divide-border font-mono text-xs">
                {logs.map((l) => (
                  <li key={l.id} className="py-2 flex gap-2">
                    <span className="text-[hsl(var(--muted-foreground))]">{l.created_at ? new Date(l.created_at).toLocaleTimeString() : ''}</span>
                    <span className={`uppercase font-semibold ${l.level === 'error' ? 'text-red-400' : l.level === 'warn' ? 'text-amber-400' : l.level === 'info' ? 'text-sky-300' : 'text-slate-400'}`}>{l.level}</span>
                    <span>{l.message}</span>
                  </li>
                ))}
              </ul>
            }
          </Card>
        </TabsContent>
        <TabsContent value="deployments">
          <Card className="p-4 bg-card border border-border">
            {deployments.length === 0 ? <div className="text-center py-6 text-[hsl(var(--muted-foreground))] text-sm">{t('no_data')}</div> :
              <ul className="divide-y divide-border">
                {deployments.map((d) => (
                  <li key={d.id} className="py-3 flex items-center gap-3">
                    <span className={`h-1.5 w-1.5 rounded-full ${d.status === 'success' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                    <div className="flex-1"><div className="font-medium">v{d.version} <span className="text-xs text-[hsl(var(--muted-foreground))] font-mono">{d.commit_hash}</span></div><div className="text-xs text-[hsl(var(--muted-foreground))]">{d.notes}</div></div>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{d.created_at ? formatDistanceToNow(new Date(d.created_at), { addSuffix: true }) : ''}</span>
                  </li>
                ))}
              </ul>
            }
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
